import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { ProductSpecification } from '../types';
import { productsApi } from '../utils/api';

interface ProductSpecificationsModalProps {
  productId: number;
  onClose: () => void;
}

export const ProductSpecificationsModal: React.FC<ProductSpecificationsModalProps> = ({ productId, onClose }) => {
  const [specifications, setSpecifications] = useState<ProductSpecification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSpecifications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await productsApi.getById(productId.toString());
        setSpecifications(response.data.specifications || []);
      } catch (err: any) {
        console.error('Failed to fetch product specifications:', err);
        setError(err.message || 'Failed to fetch product specifications');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpecifications();
  }, [productId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full z-10"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">Product Specifications</h2>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <p>Loading...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : specifications.length === 0 ? (
            <p>No specifications available for this product.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="sticky top-0 bg-white border-b">
                  <tr>
                    <th className="text-left px-2 sm:px-4 py-2 text-sm sm:text-base">Specification</th>
                    <th className="text-left px-2 sm:px-4 py-2 text-sm sm:text-base">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {specifications.map(spec => (
                    <tr key={spec.id} className="border-t">
                      <td className="px-2 sm:px-4 py-2 text-sm sm:text-base font-medium align-top">{spec.spec_key}</td>
                      <td className="px-2 sm:px-4 py-2 text-sm sm:text-base align-top">{spec.spec_value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 